/**
 * Canonical ILIKE escaping — single source of truth for every search surface.
 *
 * Postgres `ILIKE` treats `%`, `_` and `\` as pattern metacharacters. When raw
 * user input is interpolated into a `%term%` pattern without escaping, a user
 * typing `100%` or `a_b` has those characters interpreted as wildcards: wrong
 * results, plus a wider scan (a `%%%…` query is a cheap denial-of-service).
 *
 * Before this module the codebase had six divergent implementations — three of
 * them missing entirely (admin users, chat picker, seller-orders) — see
 * `.tondocs/search/SEARCH_AUDIT.md` §14.2. Every search surface (feed,
 * taxonomy, admin, chat, orders, web + mobile) MUST route user input through
 * `escapeIlike` before building a pattern.
 *
 * NOTE: this escapes ILIKE pattern metacharacters only. It is NOT
 * SQL-injection protection (the Supabase client already parameterises values),
 * and it does NOT make a value safe to interpolate into a PostgREST `.or(...)`
 * filter string, where `,` `.` `(` `)` are syntactic separators — that is a
 * separate, orthogonal concern tracked in the unification plan (Phase 1
 * follow-up). `escapeIlike` neither fixes nor worsens the `.or()` delimiter
 * edge; it strictly fixes the wildcard-injection axis.
 */
export function escapeIlike(input: string): string {
  return input.replace(/[%_\\]/g, "\\$&");
}

/**
 * Build an escaped `%term%` "contains" ILIKE pattern from raw user input.
 * Use this anywhere you would otherwise hand-write `` `%${raw}%` ``.
 */
export function ilikeContains(input: string): string {
  return `%${escapeIlike(input)}%`;
}
